import { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchModelRunById, fetchModelRunAssets } from '../redux/model-run/ModelRunThunk';

/**
 * Custom hook for managing ModelRun selection with automatic asset fetching
 * @param {Object} options - Configuration options
 * @param {string} options.modelerId - The modeler ID
 * @param {boolean} options.fetchAssets - Whether to fetch assets when ModelRun is selected
 * @param {boolean} options.fetchDetails - Whether to fetch full ModelRun details
 * @param {number} options.assetPageSize - Page size for asset fetching (default: 100)
 * @returns {Object} Hook return object
 */
export const useModelRunSelection = ({
  modelerId,
  fetchAssets = true,
  fetchDetails = false,
  assetPageSize = 100,
  onModelRunSelect = null,
  onError = null
}) => {
  const dispatch = useDispatch();
  
  // Redux state selectors
  const selectedModelRun = useSelector(state => state.modelRun?.selectedModelRun);
  const modelRunAssets = useSelector(state => state.modelRun?.selectedModelRun?.assets || []);
  const fetchingAssets = useSelector(state => state.modelRun?.fetchModelRunAssetsInProgress);
  const fetchingDetails = useSelector(state => state.modelRun?.fetchInProgress);
  const activeModeler = useSelector(state => state.modeler?.activeModeler);
  
  // Local state
  const [selectedModelRunId, setSelectedModelRunId] = useState(null);
  const [selectedModelRunData, setSelectedModelRunData] = useState(null);
  const [assetsData, setAssetsData] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Prepare model runs options for dropdown
  const modelRunOptions = (activeModeler?.modelRuns?.items || []).map(run => ({
    id: run.id,
    label: run.runName,
    value: run.id,
    run: run
  }));

  /**
   * Handle ModelRun selection from dropdown
   * @param {Object} params - Selection parameters from BaseUI Select component
   */
  const handleModelRunSelect = useCallback(async (params) => {
    if (!params.value || params.value.length === 0) {
      setSelectedModelRunId(null);
      setSelectedModelRunData(null);
      setError(null);
      return;
    }

    const selectedRun = params.value[0];
    const runId = selectedRun.id || selectedRun.value;
    const currentModelerId = modelerId || activeModeler?.id;

    if (!currentModelerId) {
      const errorMsg = 'No modeler ID available for fetching ModelRun details';
      setError(errorMsg);
      if (onError) onError(errorMsg);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setSelectedModelRunId(runId);

      // Find the run data from the options
      const runData = selectedRun.run || activeModeler?.modelRuns?.items?.find(run => run.id === runId);
      setSelectedModelRunData(runData);

      // Fetch full ModelRun details if requested
      if (fetchDetails) {
        console.log(`Fetching ModelRun details for ID: ${runId}`);
        await dispatch(fetchModelRunById(runId));
      }

      // Fetch ModelRun assets if requested
      if (fetchAssets) {
        console.log(`Fetching ModelRun assets for ModelRun: ${runId}, Modeler: ${currentModelerId}`);
        await dispatch(fetchModelRunAssets(currentModelerId, runId, assetPageSize, null));
      }

      // Call custom callback if provided
      if (onModelRunSelect) {
        onModelRunSelect(runData, runId);
      }

    } catch (err) {
      const errorMsg = `Error fetching ModelRun data: ${err.message}`;
      console.error(errorMsg, err);
      setError(errorMsg);
      if (onError) onError(errorMsg);
    } finally {
      setLoading(false);
    }
  }, [dispatch, modelerId, activeModeler, fetchAssets, fetchDetails, assetPageSize, onModelRunSelect, onError]);

  /**
   * Store assets data by ModelRun ID for multi-selection scenarios
   */
  useEffect(() => {
    if (modelRunAssets.length > 0 && selectedModelRunId) {
      setAssetsData(prev => ({
        ...prev,
        [selectedModelRunId]: modelRunAssets
      }));
    }
  }, [modelRunAssets, selectedModelRunId]);

  /**
   * Clear error when ModelRun selection changes
   */
  useEffect(() => {
    if (selectedModelRunId) {
      setError(null);
    }
  }, [selectedModelRunId]);

  /**
   * Reset state when modeler changes
   */
  useEffect(() => {
    setSelectedModelRunId(null);
    setSelectedModelRunData(null);
    setAssetsData({});
    setError(null);
  }, [modelerId, activeModeler?.id]);

  return {
    // Selection state
    selectedModelRunId,
    selectedModelRunData,
    
    // Assets data
    modelRunAssets,
    assetsData,
    
    // Loading states
    loading: loading || fetchingAssets || fetchingDetails,
    fetchingAssets,
    fetchingDetails,
    
    // Error state
    error,
    
    // Dropdown data
    modelRunOptions,
    
    // Handlers
    handleModelRunSelect,
    
    // Utilities
    getAssetsForRun: (runId) => assetsData[runId] || [],
    clearSelection: () => {
      setSelectedModelRunId(null);
      setSelectedModelRunData(null);
      setError(null);
    },
    
    // Redux data
    selectedModelRun,
    activeModeler
  };
}; 